import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { useFelhasznalo, useMuvelet } from '../felhasznalo-context.ts';
import { szam } from '../formazas.ts';
import { ElerhetosegDoboz } from '../reszek/ElerhetosegDoboz.tsx';

/**
 * Sofőri járatlap. (3.0 / 6.)
 *
 * A sofőr a telefonján a saját címeit látja kártyákon; egy címre koppintva
 * lenyílik, és beírja, mennyi volt: liter, HSO kilogramm, moslék hordó,
 * fáradt olaj hordó. A halvány szám a mezőben az, amit az iroda tervezett.
 *
 * **A mennyiségek csak tárolódnak**: nem számláznak, és az irodai Ellenőrzés
 * adatait sem írják felül — az a mai napig ugyanúgy működik.
 *
 * Innen egy gombbal átlép a szállítólevél űrlapjára, a beírt mennyiségekkel
 * együtt, és itt van az „Autó átadása a raktárnak" gomb is, hogy ne kelljen
 * menüt váltania.
 */

/** Amit a szállítólevél űrlapjának átadunk, ha innen nyílik. */
export interface SzlevKezdo {
  etterem_id: number;
  mhj: boolean;
  hso_kg?: number | null;
  hso_elvitt?: number | null;
  mo_kg?: number | null;
  mo_elvitt?: number | null;
}

interface SoforCim {
  tetel_id: number;
  etterem_id: number;
  nev: string;
  cim: string | null;
  terv_liter: number | null;
  terv_mo: number | null;
  terv_fo: number | null;
  terv_hordo: number | null;
  megjegyzes: string | null;
  sofor_liter: number | null;
  sofor_kanna_vissza: number | null;
  sofor_hso_kg: number | null;
  sofor_hso_hordo: number | null;
  sofor_mo_kg: number | null;
  sofor_mo_hordo: number | null;
  sofor_fo_hordo: number | null;
  kesz: boolean;
  szallitolevelek: string[];
}

interface NapValasz {
  datum: string;
  sofor: string | null;
  rendszam: string | null;
  cimek: SoforCim[];
}

interface NapSor {
  datum: string;
  statusz: string;
  cimek: number;
}

type SoforMezo =
  | 'sofor_liter'
  | 'sofor_kanna_vissza'
  | 'sofor_hso_kg'
  | 'sofor_hso_hordo'
  | 'sofor_mo_kg'
  | 'sofor_mo_hordo'
  | 'sofor_fo_hordo';
type TervMezo = 'terv_liter' | 'terv_hordo' | 'terv_mo' | 'terv_fo';

/**
 * A kártya mezői — ugyanabban a bontásban, ahogy a szállítólevélen is
 * állnak: olaj, HSO, moslék, fáradt olaj. A **kilós** mennyiség a
 * begyűjtő okmány alapja, ezért a hordó mellett annak is helye van.
 * (A megrendelő kérése, 2026-09-28.)
 *
 * A harmadik érték az a tervezett szám, ami halványan a mezőben látszik.
 */
const CSOPORTOK: Array<{ cim: string; mezok: Array<[SoforMezo, string, TervMezo | null]> }> = [
  {
    cim: 'Olaj',
    mezok: [
      ['sofor_liter', 'Liter', 'terv_liter'],
      ['sofor_kanna_vissza', 'Visszakapott kanna', null],
    ],
  },
  {
    cim: 'HSO — használt sütőolaj',
    mezok: [
      ['sofor_hso_kg', 'Tömeg (kg)', null],
      ['sofor_hso_hordo', 'Elvitt hordó', 'terv_hordo'],
    ],
  },
  {
    cim: 'Moslék',
    mezok: [
      ['sofor_mo_kg', 'Tömeg (kg)', null],
      ['sofor_mo_hordo', 'Elvitt hordó', 'terv_mo'],
    ],
  },
  {
    cim: 'Fáradt olaj',
    mezok: [['sofor_fo_hordo', 'Elvitt hordó', 'terv_fo']],
  },
];

export function SoforJaratlap({
  onSzallitolevel,
}: {
  onSzallitolevel: (kezdo: SzlevKezdo) => void;
}) {
  const en = useFelhasznalo();
  const kiurithet = useMuvelet('auto_kiurit');

  const [napok, setNapok] = useState<NapSor[]>([]);
  const [datum, setDatum] = useState<string>('');
  const [adat, setAdat] = useState<NapValasz | null>(null);
  const [nyitott, setNyitott] = useState<number | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  /** Hány be nem adott tétel van az autón — a leürítés megerősítéséhez. */
  const [autonDb, setAutonDb] = useState(0);
  const [kerdes, setKerdes] = useState(false);

  useEffect(() => {
    api
      .get<{ napok: NapSor[]; ma: string }>('/api/sofor-jaratlap/napok')
      .then((v) => {
        setNapok(v.napok);
        setDatum(
          (d) => d || v.napok.find((n) => n.datum === v.ma)?.datum || v.napok[0]?.datum || v.ma,
        );
      })
      .catch((h: Error) => setHiba(h.message));
  }, []);

  const autonBetolt = useCallback(async () => {
    try {
      const v = await api.get<{ autok: Array<{ sofor: string; tetelek: number }> }>(
        '/api/szallitolevel/auton',
      );
      const sajat = en?.sofor_nev ? v.autok.filter((a) => a.sofor === en.sofor_nev) : v.autok;
      setAutonDb(sajat.reduce((a, s) => a + s.tetelek, 0));
    } catch {
      /* ha ez nem jön össze, a képernyő többi része akkor is használható */
    }
  }, [en?.sofor_nev]);

  const betolt = useCallback(async () => {
    if (!datum) return;
    try {
      setAdat(await api.get<NapValasz>(`/api/sofor-jaratlap/${datum}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A járatlapot nem sikerült betölteni.');
    }
  }, [datum]);

  useEffect(() => {
    void betolt();
    void autonBetolt();
  }, [betolt, autonBetolt]);

  /** Egy mennyiség rögzítése. Minden mező külön kérés. (Megbízás 2.0 / 2.) */
  async function ment(tetel_id: number, mezo: string, ertek: string) {
    try {
      await api.patch(`/api/sofor-jaratlap/tetel/${tetel_id}/${mezo}`, { ertek });
      setHiba(null);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
      await betolt();
    }
  }

  function kiuritesGomb() {
    if (autonDb === 0) {
      setUzenet('Nincs nálad be nem adott tétel.');
      return;
    }
    setKerdes(true);
  }

  async function autotKiurit() {
    setKerdes(false);
    try {
      await api.post('/api/szallitolevel/auto-kiurites', { sofor: en?.sofor_nev ?? undefined });
      setUzenet(`${autonDb} tétel átkerült a raktárba.`);
      setHiba(null);
      await autonBetolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az átadás nem sikerült.');
    }
  }

  /**
   * A beírt mennyiségeket átvisszük az űrlapra, hogy ne kelljen újra
   * begépelni.
   *
   * **A liter és a kanna szándékosan nem megy át**: a szállítólevél
   * begyűjtő okmány, a HSO-ról és a moslékról szól. A kiszállított olaj a
   * számlázásba tartozik, nem lesz belőle „általános" szállítólevél.
   * (A megrendelő döntése, 2026-09-28.)
   */
  function szlevre(c: SoforCim, mhj: boolean) {
    onSzallitolevel({
      etterem_id: c.etterem_id,
      mhj,
      hso_kg: mhj ? null : c.sofor_hso_kg,
      hso_elvitt: mhj ? null : c.sofor_hso_hordo,
      mo_kg: mhj ? null : c.sofor_mo_kg,
      mo_elvitt: mhj ? null : c.sofor_mo_hordo,
    });
  }

  const cimek = adat?.cimek ?? [];
  const keszDb = cimek.filter((c) => c.kesz).length;
  const osszeg = (mezo: SoforMezo) =>
    cimek.reduce((a, c) => a + (c[mezo] ?? 0), 0);

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Járatlap</span>
        <select
          value={datum}
          onChange={(e) => {
            setDatum(e.target.value);
            setNyitott(null);
          }}
        >
          {napok.map((n) => (
            <option key={n.datum} value={n.datum}>
              {n.datum} · {n.cimek} cím
            </option>
          ))}
          {napok.length === 0 && <option value="">nincs nap</option>}
        </select>
        <div className="stats">
          <div>
            Cím<strong>{cimek.length}</strong>
          </div>
          <div>
            Kész<strong>{keszDb}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      <div className="actbar" style={{ marginTop: 12 }}>
        {kiurithet &&
          (kerdes ? (
            <>
              <button className="btn gold sm" onClick={() => void autotKiurit()}>
                Biztos? {autonDb} tétel — kattints újra
              </button>
              <button className="btn sm" onClick={() => setKerdes(false)}>
                Mégse
              </button>
            </>
          ) : (
            <button className="btn sm" onClick={kiuritesGomb}>
              Autó átadása a raktárnak
            </button>
          ))}
        <span className="hint">
          {en?.sofor_nev
            ? `${en.sofor_nev}${adat?.rendszam ? ` · ${adat.rendszam}` : ''} — a mennyiségek egyelőre csak tárolódnak. A tételekből egy gombbal kiállítható a szállítólevél.`
            : 'Nem vagy sofőrhöz rendelve, ezért a nap összes címét látod.'}
        </span>
      </div>

      <div id="sjlista">
        {cimek.map((c) => (
          <div
            className={`sjkart${c.kesz ? ' kesz' : ''}${nyitott === c.tetel_id ? ' nyit' : ''}`}
            key={c.tetel_id}
          >
            <div
              className="sjfej"
              onClick={() => setNyitott(nyitott === c.tetel_id ? null : c.tetel_id)}
            >
              <b>{c.nev}</b>
              {c.kesz && <span className="sjok">kész</span>}
              <span className="sjcim">{c.cim ?? ''}</span>
            </div>

            {nyitott === c.tetel_id && (
              <div className="sjbody">
                {CSOPORTOK.map((cs) => (
                  <div className="sjcsop" key={cs.cim}>
                    <span className="sjcsopcim">{cs.cim}</span>
                    <div className="r4x">
                      {cs.mezok.map(([mezo, cimke, terv]) => (
                        <div key={mezo}>
                          <label>{cimke}</label>
                          <input
                            inputMode="decimal"
                            onFocus={(e) => e.currentTarget.select()}
                            defaultValue={c[mezo] === null ? '' : szam(c[mezo], 0)}
                            placeholder={terv && c[terv] ? szam(c[terv], 0) : ''}
                            onBlur={(e) => void ment(c.tetel_id, mezo, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* A helyszínen megtudott telefonszám itt rögzíthető. (3.0 / 1.) */}
                <ElerhetosegDoboz etterem_id={c.etterem_id} csakUj cel="szallito" />

                {c.megjegyzes && <div className="hint">Megjegyzés: {c.megjegyzes}</div>}
                {c.szallitolevelek.length > 0 && (
                  <div className="hint">Mai okmány: {c.szallitolevelek.join(', ')}</div>
                )}

                <div className="btns">
                  <button className="btn pri sm" onClick={() => szlevre(c, false)}>
                    Szállítólevél kiállítása
                  </button>
                  <button className="btn sm" onClick={() => szlevre(c, true)}>
                    Meghiúsult
                  </button>
                  <span className="hint">
                    A szállítólevél a <b>HSO-ból és a moslékból</b> készül. A kiszállított liter és
                    a kanna a számlázásba megy — okmány nem készül róla.
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}

        {cimek.length === 0 && (
          <div className="blank" style={{ marginTop: 16 }}>
            Erre a napra nincs hozzád rendelt cím. A Szállítólevél menüpontból bármelyik partnerhez
            kiállíthatsz.
          </div>
        )}
      </div>

      {cimek.length > 0 && (
        <div className="hint" style={{ marginTop: 10 }}>
          Ezen a napon rögzítve: {szam(osszeg('sofor_liter'), 0)} liter ·{' '}
          {szam(osszeg('sofor_hso_kg'), 0)} kg HSO ({szam(osszeg('sofor_hso_hordo'), 0)} hordó) ·{' '}
          {szam(osszeg('sofor_mo_kg'), 0)} kg moslék ({szam(osszeg('sofor_mo_hordo'), 0)} hordó) ·{' '}
          {szam(osszeg('sofor_fo_hordo'), 0)} FO hordó
        </div>
      )}
    </>
  );
}
